import seedrandom from 'seedrandom';

/**
 * Deterministic RNG wrapper for the generator.
 *
 * Every generated house must be reproducible from seed + constraints. Using
 * `Math.random()` anywhere in the generation pipeline breaks that guarantee,
 * so we route all randomness through this module. The ESLint rule under
 * `eslint.config.js` rejects `Math.random()` in `src/generator/**`.
 *
 * Substreams
 * ----------
 * The pipeline passes a root RNG through many stages (lot, footprint, rooms,
 * openings, roof, accessories...). If we drew every number from a single
 * stream, re-ordering or inserting calls in one stage would shift every
 * downstream value and invalidate saved seed snapshots. `rng.fork(label)`
 * returns a child RNG whose stream is derived from the parent's seed plus
 * the label, so a change inside one stage can't move another stage's output.
 */
export interface Rng {
  /** Uniform [0, 1). */
  next(): number;
  /** Uniform integer in [lo, hi]. Inclusive on both ends. */
  int(lo: number, hi: number): number;
  /** Uniform float in [lo, hi). */
  range(lo: number, hi: number): number;
  /** Returns true with the given probability (0..1). */
  chance(p: number): boolean;
  /** Picks one item uniformly. Throws on empty arrays. */
  pick<T>(items: readonly T[]): T;
  /** Weighted pick. Weights must be non-negative; zero weights are skipped. */
  weighted<T>(items: readonly T[], weights: readonly number[]): T;
  /** Durstenfeld shuffle producing a new array; original is not mutated. */
  shuffle<T>(items: readonly T[]): T[];
  /** Derive an independent substream tagged `label`. */
  fork(label: string): Rng;
  /** The seed used to initialize this RNG (for debugging/display). */
  readonly seed: string;
}

class SeededRng implements Rng {
  private readonly rng: seedrandom.PRNG;
  public readonly seed: string;

  constructor(seed: string) {
    this.seed = seed;
    this.rng = seedrandom(seed);
  }

  next(): number {
    return this.rng();
  }

  int(lo: number, hi: number): number {
    if (hi < lo) [lo, hi] = [hi, lo];
    return Math.floor(this.rng() * (hi - lo + 1)) + lo;
  }

  range(lo: number, hi: number): number {
    if (hi < lo) [lo, hi] = [hi, lo];
    return lo + this.rng() * (hi - lo);
  }

  chance(p: number): boolean {
    return this.rng() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('pick() on empty array');
    return items[Math.floor(this.rng() * items.length)]!;
  }

  weighted<T>(items: readonly T[], weights: readonly number[]): T {
    if (items.length === 0) throw new Error('weighted() on empty array');
    if (items.length !== weights.length) throw new Error('weighted(): length mismatch');
    let total = 0;
    for (const w of weights) {
      if (w < 0 || !Number.isFinite(w)) throw new Error('weighted(): invalid weight');
      total += w;
    }
    if (total <= 0) throw new Error('weighted(): all weights are zero');
    let roll = this.rng() * total;
    for (let i = 0; i < items.length; i++) {
      roll -= weights[i]!;
      if (roll < 0) return items[i]!;
    }
    return items[items.length - 1]!;
  }

  shuffle<T>(items: readonly T[]): T[] {
    const copy = items.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [copy[i], copy[j]] = [copy[j]!, copy[i]!];
    }
    return copy;
  }

  fork(label: string): Rng {
    return new SeededRng(`${this.seed}::${label}`);
  }
}

export function createRng(seed: string): Rng {
  if (!seed) throw new Error('createRng: empty seed');
  return new SeededRng(seed);
}
